import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    useSsDispatch as useDispatch,
    useSsSelector as useSelector,
} from '~SpectateStore';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import Skeleton from './Skeleton';
import Banners from '../Banners';

import {
    removeSelectionFromBetslip,
} from '~Betslip/interface/interactions';

import {
    updateSelection,
} from '~Betslip/slices/userSelectionsSlice';

import {
    selectUserSelectionById,
    selectSinglesStake,
    selectCountUserSelections,
} from '~Betslip/selectors/userSelectionsSelectors';

import {
    selectSelectionDetailsById,
} from '~Betslip/selectors/selectionDetailsSelectors';

import {
    selectShowPriceChange,
} from '~Betslip/selectors/notificationSelectors';

import {
    selectShowContingencyMarker,
} from '~Betslip/selectors/contingenciesSelectors';

import {
    selectDisplaySinglesContent,
    selectUiMode,
} from '~Betslip/selectors/uiSelectors';

import {
    selectSinglePayout,
} from '~Betslip/selectors/singlesPayoutSelectors';

import {
    useKeyboard,
} from '~Betslip/store/keyboardContext';
import {
    useIsEachwayAllowed,
    useOddsFormat,
} from '~SpectateStore/hooks/userHooks';
import { decimalPriceOdds } from '~shared/utils/selections';
import Time from '~shared/ui/Time/Time';
import { getLocalizedDateFormat } from '~shared/utils/date-time';
import { DATE_TIME_FORMAT_HM } from '~shared/utils/constants';
import { alterMarketName } from '~shared/utils/market-utils';
import { getOddsBoostSelection } from '~spa/Components/OddsBoost/utils';
import SwitchableOdds from '~spa/Components/SwitchableOdds';

import StakeField from '../StakeField';
import Payout from '../Payout';

import {
    selectHasTotalSinglesExceedBalance,
} from '~Betslip/selectors/userSelectors';

import './styles.scss';

const Selection = ({ id }) => {
    const [ t ] = useTranslation();
    const dispatch = useDispatch();
    const keyboardRef = useRef();
    const { setKeyboardContainerRef } = useKeyboard();
    const userSelection = useSelector((state) => selectUserSelectionById(state, id));
    const selectionDetails = useSelector((state) => selectSelectionDetailsById(state, id));
    const showContingencyMarker = useSelector((state) => selectShowContingencyMarker(state, id));
    const isSinglesTab = useSelector(selectDisplaySinglesContent);
    const singlesStake = useSelector(selectSinglesStake);
    const payout = useSelector((state) => selectSinglePayout(state, id, userSelection?.free_bet_id));
    const { priceChanged, priceChangeDirection} = useSelector((state) => selectShowPriceChange(state, id));
    const { isDepositNeeded } = useSelector(selectHasTotalSinglesExceedBalance);
    const oddsFormat = useOddsFormat();
    const isEachwayAllowed = useIsEachwayAllowed();
    const count = useSelector(selectCountUserSelections);
    const uiMode = useSelector(selectUiMode);

    useEffect(() => {
        const singlesUserStake = userSelection.stake === singlesStake;
        if (uiMode === 'baby' && isSinglesTab && !singlesUserStake && count > 1) {
            dispatch(updateSelection({
                id,
                stake: singlesStake,
            }));
        }
    }, [isSinglesTab]);

    const openKeyboard = () => {
        setKeyboardContainerRef(keyboardRef.current);
    };

    if (!selectionDetails) {
        return <Skeleton />;
    }

    const {
        event_name,
        market_name,
        selection_name,
        selection_names,
        each_way_allowed,
        decimal_price,
        fraction_price,
        american_price,
        sp_only,
        sport,
        scheduled_start,
        place_odds_divisor,
        places_paid,
        each_way_plus,
        market_id,
        suspended,
        selection_long_name,
        hometeam,
        awayteam,
        is_odds_boost,
    } = selectionDetails;

    const isSp = sp_only || userSelection.sp_selected;

    const updateUserSelection = (data) => dispatch(
        updateSelection({id, ...data})
    );

    const updateUserChoices = (stake, each_way_selected, free_bet_id) => {
        const fbStake = (free_bet_id) ? 0 : stake;
        updateUserSelection({ stake: fbStake, each_way_selected, free_bet_id });
    };

    const eventName = () => {
        const classes = 'fullslip__event-name';
        const isRacing = (['horse-racing', 'greyhound-racing', 'trotting'].indexOf(sport) > -1);

        return <div className={classes} data-test-id="betslip;event-name">
            {event_name}
            {
                isRacing
                ?
                    <>
                        {' '}
                        <Time dateTime={scheduled_start} format={getLocalizedDateFormat(DATE_TIME_FORMAT_HM)} />
                    </>
                : null
            }
        </div>;
    };

    const marketName = () => {
        const classes = 'fullslip__market-name';
        const newMarketName = alterMarketName(market_name, hometeam, awayteam, sport);

        return <div className={classes} data-test-id="betslip;market-name">
            {newMarketName}
        </div>;
    };

    const selectionName = () => {
        const classes = 'fullslip__selection-name';

        if (!userSelection.is_cast_market && selection_long_name) {
            return <div className={classes}>
                {
                    selection_long_name.map((s) => (
                        <div key={s.selection}>
                            <span>{`${s.selection} `}</span>
                            <span className="fullslip__long-selection-market">{s.market}</span>
                        </div>
                    ))
                }
            </div>;
        }

        if (!userSelection.is_cast_market) {
            return <div className={classes} data-test-id="betslip;selection-name">
                {selection_name}
            </div>;
        }

        const isAny = userSelection.is_any;

        return (<div className={classes} data-test-id="betslip;selection-name">
            {
                selection_names.map((name, index) => (
                    <div key={index}>
                        {name}
                        {isAny ? '' : <span className="fullslip__selection-hint"> {t(`betslip.fctc_header_${index + 1}`)}</span>}
                    </div>
                ))
            }
        </div>);
    };

    const removeSelectionCta = () => {
        const classes = classNames(
            'fullslip__selection-remove',
            showContingencyMarker && 'fullslip__selection-remove--contingency',
        );

        return (
            <div
                className={classes}
                data-spectate-product="new-betslip;remove-from-betslip"
                onClick={
                    () => removeSelectionFromBetslip(id)
                }
            >
                <i className="fullslip__trash-icon fullslip__trash-icon--medium"></i>
            </div>
        );
    };

    const stakeField = () => {
        if (!isSinglesTab) {
            return null;
        }

        const gtmDetails = {
            source: 'new-betslip',
            betType: '',
        };

        return (
            <StakeField
                stake={userSelection.stake}
                each_way_allowed={each_way_allowed && isEachwayAllowed}
                each_way_selected={userSelection.each_way_selected}
                onChangeCallback={updateUserChoices}
                initKeyboard={openKeyboard}
                onClickHandler={openKeyboard}
                disabled={suspended}
                freebetId={userSelection.free_bet_id}
                betslipId={id}
                gtmDetails={gtmDetails}
                depositRequired={isDepositNeeded}
            />
        );
    };

    const selectionPrice = () => {
        const defaultRacingMarkets = [1, 121, 122];
        const spDropdownAvailable = !sp_only && defaultRacingMarkets.includes(market_id);

        if (suspended) {
            return (
                <div className="fullslip__suspended" data-test-id="betslip;selection-price">
                    {t('betslip.suspended')}
                </div>
            );
        }

        if(is_odds_boost) {
            selectionDetails.sport_slug = sport;
            const oddsBoostSelection = getOddsBoostSelection(selectionDetails);

            return (
                <div className="fullslip__odds-boost-selection" data-test-id="betslip;odds-boost-selection">
                    <span className="fullslip__odds-boost-selection--was-price">
                        <SwitchableOdds selection={oddsBoostSelection} />
                    </span>
                    <span className="fullslip__odds-boost-selection--now-price">{oddsOrSP()}</span>
                    <span className="fullslip__odds-boost-selection--odds-boost-indicator">
                        <span className="fullslip__odds-boost-selection--odds-boost-indicator-icon"></span>
                    </span>
                </div>
            );
        }

        const classes = classNames('fullslip__selection-price', {
            'fullslip__odds-change': priceChanged,
            [`fullslip__odds-change--${priceChangeDirection}`]: priceChangeDirection,
        });

        return (
            <div
                className={classes}
                data-test-id="betslip;selection-price"
            >
                { spDropdownAvailable ? spDropdown() : oddsOrSP() }
            </div>
        );
    };

    const oddsOrSP = () => {
        if (sp_only || decimal_price === null || userSelection.sp_selected) {
            return t('betslip.sp');
        }

        return formattedOdds();
    };

    const formattedOdds = () => {
        switch (oddsFormat) {
            case 'AMERICAN':
                return american_price;
            case 'FRACTION':
                return fraction_price;
            default:
                return decimalPriceOdds(decimal_price);
        }
    };

    const spDropdown = () => {

        const classes = classNames('fullslip__sp-dropdown', {
            'fullslip__sp-dropdown--odds-change': priceChanged,
        });

        return (
            <div className={classes}>
                <select
                    name="spDropdown"
                    className="fullslip__sp-dropdown-select"
                    value={oddsOrSP()}
                    onChange={(e) => {
                        const isSpSelected = e.target.value === t('betslip.sp');
                        dispatch(updateUserSelection({
                            sp_selected: isSpSelected,
                        }));
                    }}
                >
                    <option>{formattedOdds()}</option>
                    <option>{t('betslip.sp')}</option>
                </select>
            </div>
        );
    };

    const ewTerms = () => {
        if (!each_way_allowed || !isEachwayAllowed) {
            return null;
        }

        const isEachWayPlus = Boolean(Number(each_way_plus)) === true;
        const ewTermsClasses = classNames('fullslip__ew-terms', {
            'fullslip__ew-terms-full': !isSinglesTab,
        });

        const ewConditionClasses = classNames('fullslip__ew-condition', {
            'fullslip__ew-condition--has-ew-plus': isEachWayPlus,
        });

        return (
            <span className={ewTermsClasses} dir="auto">
                {t('racing.ew_terms')}:
                <span className={ewConditionClasses} dir="auto">
                    {` ${t('racing.ew_conditions', {
                        divisor: place_odds_divisor,
                        places: places_paid,
                    })}`}
                </span>
                {isEachWayPlus
                    ? <span className="fullslip__ew-plus">{t('racing.ew_plus')}</span>
                    : null
                }
            </span>
        );
    };

    const payoutElement = () => {
        if (!isSinglesTab) {
            return null;
        }

        return <Payout payout={payout} />;
    };

    const keyboard = () => {
        if (!isSinglesTab) {
            return null;
        }

        return <div ref={keyboardRef} />;
    };

    return (<>
        <div className="fullslip__selection" data-test-id={`betslip;selection-${id}`}>
            {removeSelectionCta()}
            <div className="fullslip__selection-body">
                <Banners selectionDetails={selectionDetails} isSp={isSp} />
                <div className="fullslip__selection-details">
                    {eventName()}
                    {selectionName()}
                    {marketName()}
                    {ewTerms()}
                    {selectionPrice()}
                    {stakeField()}
                    {payoutElement()}
                </div>
            </div>
        </div>
        {keyboard()}
    </>);
};

Selection.propTypes = {
    id: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
    ]).isRequired,
};

export default Selection;
